import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToastModule } from 'primeng/toast';
import { Currency } from '../../../../core/models/currency';
import { GuardConfiguration, GuardConfigurationStatus } from '../../../../core/models/guard-configuration';
import { CurrencyService } from '../../../../core/services/currency.service';
import { GuardConfigurationService } from '../../../../core/services/guard-configuration.service';
import { RequiredMarkDirective } from '../../../../shared/directives/required-mark.directive';

type GuardConfigurationControl =
  | 'currencyId'
  | 'startDate'
  | 'endDate'
  | 'status'
  | 'laborable'
  | 'nonLaborable'
  | 'especial';

const dateRangeValidator = (control: AbstractControl): ValidationErrors | null => {
  const startDate = control.get('startDate')?.value;
  const endDate = control.get('endDate')?.value;

  if (!startDate || !endDate) {
    return null;
  }

  return new Date(endDate).getTime() >= new Date(startDate).getTime()
    ? null
    : { dateRange: true };
};

const costCombinationValidator = (control: AbstractControl): ValidationErrors | null => {
  const laborable = control.get('laborable')?.value;
  const nonLaborable = control.get('nonLaborable')?.value;
  const especial = control.get('especial')?.value;

  if ([laborable, nonLaborable, especial].some((value) => value === null || value === undefined || value === '')) {
    return null;
  }

  return [laborable, nonLaborable, especial].every((value) => Number(value) === 0)
    ? { allCostsZero: true }
    : null;
};

@Component({
  selector: 'em-guard-configuration-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    CalendarModule,
    DropdownModule,
    InputNumberModule,
    ButtonModule,
    ToastModule,
    RequiredMarkDirective
  ],
  providers: [MessageService],
  templateUrl: './guard-configuration-form.component.html',
  styleUrl: './guard-configuration-form.component.scss'
})
export class GuardConfigurationFormComponent implements OnInit {
  isSaving = false;
  isEditMode = false;
  activeCurrencies: Currency[] = [];
  statusOptions: { label: string; value: GuardConfigurationStatus }[] = [];
  private readonly destroyRef = inject(DestroyRef);
  private guardConfiguration: GuardConfiguration | undefined;
  private statusSyncedFromPastEndDate = false;
  private updatingStatusFromEndDate = false;
  private readonly duplicateOverlapKey = 'layout.configurations.guardConfiguration.validation.duplicateOverlap';
  private readonly duplicateOverlapFallbacks: Record<string, string> = {
    ar: 'توجد تكلفة مناوبة بنفس التسمية وتتداخل مع هذا النطاق الزمني',
    en: 'An on-call cost with the same label already overlaps this date range', // i18n-ignore (per-locale fallback)
    es: 'Ya existen guardias con la misma etiqueta y un rango de fechas solapado' // i18n-ignore (per-locale fallback)
  };
  private readonly pastEndDateCreateKey = 'layout.configurations.guardConfiguration.validation.pastEndDateCreate';
  private readonly pastEndDateCreateFallbacks: Record<string, string> = {
    ar: 'تاريخ النهاية سابق',
    en: 'End date is past', // i18n-ignore (per-locale fallback)
    es: 'La fecha de fin ya pasó' // i18n-ignore (per-locale fallback)
  };

  form = this.fb.group(
    {
      currencyId: [null as number | null, Validators.required],
      startDate: [null as Date | null, Validators.required],
      endDate: [null as Date | null, Validators.required],
      status: [GuardConfigurationStatus.ACTIVE, Validators.required],
      laborable: [null as number | null, [Validators.required, Validators.min(0)]],
      nonLaborable: [null as number | null, [Validators.required, Validators.min(0)]],
      especial: [null as number | null, [Validators.required, Validators.min(0)]]
    },
    { validators: [dateRangeValidator, costCombinationValidator] }
  );

  constructor(
    private fb: FormBuilder,
    private guardConfigurationService: GuardConfigurationService,
    private currencyService: CurrencyService,
    private messageService: MessageService,
    private translate: TranslateService,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig
  ) {}

  private buildStatusOptions(): void {
    this.statusOptions = [
      {
        label: this.translate.instant('layout.configurations.guardConfiguration.statuses.ACTIVE'),
        value: GuardConfigurationStatus.ACTIVE
      },
      {
        label: this.translate.instant('layout.configurations.guardConfiguration.statuses.INACTIVE'),
        value: GuardConfigurationStatus.INACTIVE
      }
    ];
  }

  ngOnInit(): void {
    this.buildStatusOptions();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.buildStatusOptions());
    this.loadActiveCurrencies();
    this.guardConfiguration = this.config.data?.guardConfiguration;
    this.isEditMode = this.config.data?.mode === 'edit' && !!this.guardConfiguration;
    this.patchFormForEdit();
    this.form.controls.endDate.valueChanges.subscribe(() => {
      this.validateEndDateForCreate();
      this.syncStatusFromEndDate();
    });
    this.form.controls.status.valueChanges.subscribe(() => {
      if (!this.updatingStatusFromEndDate) {
        this.statusSyncedFromPastEndDate = false;
      }
    });
    this.validateEndDateForCreate();
    this.syncStatusFromEndDate();
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.validateEndDateForCreate();
    if (this.hasPastEndDateCreateError()) {
      this.form.controls.endDate.markAsTouched();
      return;
    }

    const payload: GuardConfiguration = {
      currencyId: Number(this.form.controls.currencyId.value),
      startDate: this.toIsoDate(this.form.controls.startDate.value as Date),
      endDate: this.toIsoDate(this.form.controls.endDate.value as Date),
      status: this.form.controls.status.value as GuardConfigurationStatus,
      laborable: Number(this.form.controls.laborable.value),
      nonLaborable: Number(this.form.controls.nonLaborable.value),
      especial: Number(this.form.controls.especial.value)
    };

    this.isSaving = true;
    const request =
      this.isEditMode && this.guardConfiguration?.id
        ? this.guardConfigurationService.update(this.guardConfiguration.id, payload)
        : this.guardConfigurationService.create(payload);

    request.subscribe({
      next: (guardConfiguration) => {
        this.isSaving = false;
        this.ref.close({ type: 'success', data: guardConfiguration });
      },
      error: (error) => {
        this.isSaving = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('layout.configurations.guardConfiguration.notifications.errorTitle'),
          detail: this.resolveSaveErrorMessage(error)
        });
      }
    });
  }

  onCancel(): void {
    this.ref.close();
  }

  isInvalid(controlName: GuardConfigurationControl): boolean {
    const control = this.form.controls[controlName];
    return control.invalid && (control.dirty || control.touched);
  }

  hasControlError(controlName: GuardConfigurationControl, errorName: string): boolean {
    const control = this.form.controls[controlName];
    return control.hasError(errorName) && (control.dirty || control.touched);
  }

  hasDateRangeError(): boolean {
    return (
      this.form.hasError('dateRange') &&
      (this.form.controls.endDate.dirty || this.form.controls.endDate.touched)
    );
  }

  hasPastEndDateCreateError(): boolean {
    return (
      this.form.controls.endDate.hasError('pastEndDateCreate') &&
      (this.form.controls.endDate.dirty || this.form.controls.endDate.touched)
    );
  }

  hasAllCostsZeroError(): boolean {
    return (
      this.form.hasError('allCostsZero') &&
      (this.form.controls.laborable.dirty ||
        this.form.controls.laborable.touched ||
        this.form.controls.nonLaborable.dirty ||
        this.form.controls.nonLaborable.touched ||
        this.form.controls.especial.dirty ||
        this.form.controls.especial.touched)
    );
  }

  private loadActiveCurrencies(): void {
    this.currencyService.getActiveCurrencies().subscribe({
      next: (currencies) => {
        this.activeCurrencies = currencies;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('layout.configurations.guardConfiguration.notifications.errorTitle'),
          detail: this.translate.instant('layout.configurations.guardConfiguration.notifications.currencyLoadError')
        });
      }
    });
  }

  private resolveSaveErrorMessage(error: unknown): string {
    const message = (error as { error?: { message?: string } })?.error?.message;
    if (message?.includes('same label') && message.includes('overlaps')) {
      return this.translateWithFallback(this.duplicateOverlapKey, this.duplicateOverlapFallbacks);
    }
    if (message?.includes('end date before today')) {
      return this.translateWithFallback(this.pastEndDateCreateKey, this.pastEndDateCreateFallbacks);
    }
    return message || this.translate.instant('layout.configurations.guardConfiguration.notifications.saveError');
  }

  private translateWithFallback(key: string, fallbacks: Record<string, string>): string {
    const translatedValue = this.translate.instant(key);
    if (translatedValue && translatedValue !== key) {
      return translatedValue;
    }

    const language = (this.translate.currentLang || this.translate.defaultLang || 'en').split('-')[0];
    return fallbacks[language] || fallbacks['en'];
  }

  private patchFormForEdit(): void {
    if (!this.guardConfiguration) {
      return;
    }

    this.form.patchValue({
      currencyId: this.guardConfiguration.currencyId,
      startDate: this.toDate(this.guardConfiguration.startDate),
      endDate: this.toDate(this.guardConfiguration.endDate),
      status: this.guardConfiguration.status as GuardConfigurationStatus,
      laborable: this.guardConfiguration.laborable,
      nonLaborable: this.guardConfiguration.nonLaborable,
      especial: this.guardConfiguration.especial
    });
  }

  private toIsoDate(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  private isPastEndDate(endDate: Date | string | null | undefined): boolean {
    const parsedEndDate = this.toDate(endDate);
    return !!parsedEndDate && this.startOfDay(parsedEndDate).getTime() < this.startOfDay(new Date()).getTime();
  }

  private setEndDatePastCreateError(): void {
    const errors = this.form.controls.endDate.errors || {};
    this.form.controls.endDate.setErrors({ ...errors, pastEndDateCreate: true });
  }

  private validateEndDateForCreate(): void {
    this.clearEndDatePastCreateError();
    if (!this.isEditMode && this.isPastEndDate(this.form.controls.endDate.value)) {
      this.setEndDatePastCreateError();
    }
  }

  private syncStatusFromEndDate(): void {
    if (!this.isEditMode) {
      return;
    }

    if (this.isPastEndDate(this.form.controls.endDate.value)) {
      this.setStatusFromEndDate(GuardConfigurationStatus.INACTIVE);
      this.statusSyncedFromPastEndDate = true;
      return;
    }

    if (this.statusSyncedFromPastEndDate) {
      this.setStatusFromEndDate(GuardConfigurationStatus.ACTIVE);
      this.statusSyncedFromPastEndDate = false;
    }
  }

  private setStatusFromEndDate(status: GuardConfigurationStatus): void {
    this.updatingStatusFromEndDate = true;
    this.form.controls.status.setValue(status, { emitEvent: false });
    this.updatingStatusFromEndDate = false;
  }

  private clearEndDatePastCreateError(): void {
    const errors = this.form.controls.endDate.errors;
    if (!errors?.['pastEndDateCreate']) {
      return;
    }

    const remainingErrors = { ...errors };
    delete remainingErrors['pastEndDateCreate'];
    this.form.controls.endDate.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private toDate(value: string | Date | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
}
